// 応答本文を JSON として読む。JSON でないときは HTTP 状態から分かる文言にする。
//
// アップロード上限を超えたときの PHP は、起動時の警告を JSON の前に出力することがあり、
// リバースプロキシが 413 を空本文で返すこともある。素の `response.json()` だと
// どちらも「Unexpected end of JSON input」になり、利用者に原因が伝わらない。
export async function readJsonResponse(response) {
  let text = '';
  try {
    text = await response.text();
  } catch (error) {
    return { ok: false, error: apiTransportMessage(response.status) };
  }

  const parsed = parseJsonLoosely(text);
  if (parsed && typeof parsed === 'object') {
    return parsed;
  }
  return { ok: false, error: apiTransportMessage(response.status) };
}

// PHP の警告などが前に付いた本文からも JSON 部分を拾う。
function parseJsonLoosely(text) {
  const body = String(text || '').trim();
  if (body === '') {
    return null;
  }
  try {
    return JSON.parse(body);
  } catch (error) {
    // 続けて後方の { ... } を探す。
  }
  const start = body.indexOf('{');
  const end = body.lastIndexOf('}');
  if (start === -1 || end <= start) {
    return null;
  }
  try {
    return JSON.parse(body.slice(start, end + 1));
  } catch (error) {
    return null;
  }
}

function apiTransportMessage(status) {
  const lang = typeof document === 'undefined' ? 'ja' : document.documentElement.lang || 'ja';
  const ja = lang.toLowerCase().startsWith('ja');
  if (status === 413) {
    return ja
      ? 'ファイルサイズがサーバーの送信上限を超えています。画像を縮小してから試してください。'
      : 'The file exceeds the server request size limit. Please shrink it and try again.';
  }
  if (status === 401 || status === 403) {
    return ja
      ? 'ログイン状態を確認できませんでした。ページを再読み込みしてください。'
      : 'The session could not be verified. Please reload the page.';
  }
  return ja
    ? `サーバーから正しい応答を受け取れませんでした（HTTP ${status}）。`
    : `The server returned an unreadable response (HTTP ${status}).`;
}

export function createApiClient({ csrfToken, editSessionToken }) {
  const requestTimeoutMs = 30000;
  const uploadTimeoutMs = 120000;
  // ページ読み込み時の <meta> から読んだ値を起点にする。PHP セッションが切れて
  // サーバ側でトークンが作り直されたときは、api/csrf.php から取り直して差し替える。
  let currentCsrfToken = csrfToken || '';
  let csrfRefreshPromise = null;

  async function fetchWithTimeout(path, options, timeoutMs = requestTimeoutMs) {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);
    try {
      return await fetch(path, { ...options, signal: controller.signal });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        // オフライン復元の対象になるよう、fetch のネットワーク失敗と同じ型にする。
        throw new TypeError('Request timed out');
      }
      throw error;
    } finally {
      window.clearTimeout(timeoutId);
    }
  }

  function dispatchAuthRequired(error, status) {
    if (typeof window === 'undefined' || (status !== 401 && error !== 'Login required')) {
      return;
    }
    window.dispatchEvent(
      new CustomEvent('outline-editor:auth-required', {
        detail: { message: error, status: 401 },
      })
    );
  }

  function throwApiError(data, status) {
    const message = data.error || 'Request failed';
    const authRequired = status === 401 || message === 'Login required';
    dispatchAuthRequired(message, status);
    throw Object.assign(new Error(message), { status, authRequired });
  }

  function editSessionHeaders() {
    if (!editSessionToken) {
      return {};
    }
    return { 'X-Edit-Session': editSessionToken };
  }

  function isCsrfError(data, status) {
    return status === 403 && (data?.error || '') === 'Invalid CSRF token';
  }

  // 新しいトークンを、次のリクエストだけでなくページ内の hidden input にも反映する。
  // ログアウトやファイル操作などは通常の form 送信のため、そちらも古いままだと落ちる。
  function applyCsrfToken(token) {
    currentCsrfToken = token;
    if (typeof document === 'undefined') {
      return;
    }
    const meta = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]');
    if (meta) {
      meta.content = token;
    }
    document.querySelectorAll<HTMLInputElement>('input[name="csrf_token"]').forEach((input) => {
      input.value = token;
    });
  }

  // 403 が並列に返ってきても取得は1回にまとめる。
  function refreshCsrfToken() {
    if (!csrfRefreshPromise) {
      csrfRefreshPromise = (async () => {
        const response = await fetchWithTimeout('api/csrf.php', {
          credentials: 'same-origin',
          headers: editSessionHeaders(),
        });
        const data = await readJsonResponse(response);
        if (!response.ok || !data.ok || typeof data.csrf_token !== 'string' || !data.csrf_token) {
          throwApiError(data, response.status);
        }
        applyCsrfToken(data.csrf_token);
        return data.csrf_token;
      })().finally(() => {
        csrfRefreshPromise = null;
      });
    }
    return csrfRefreshPromise;
  }

  // CSRF トークンが古くなっていたら取り直して、同じリクエストを1回だけやり直す。
  async function sendWithCsrfRetry(send) {
    const response = await send(currentCsrfToken);
    const data = await readJsonResponse(response);
    if (response.ok && data.ok) {
      return data;
    }
    if (!isCsrfError(data, response.status)) {
      throwApiError(data, response.status);
    }

    let refreshed;
    try {
      refreshed = await refreshCsrfToken();
    } catch (error) {
      // 取り直しにも失敗したら、元の CSRF エラーをそのまま返す。
      // ログインが切れている場合は、ここで 401 の扱いになる。
      if (error && error.authRequired) {
        throw error;
      }
      throwApiError(data, response.status);
    }

    const retryResponse = await send(refreshed);
    const retryData = await readJsonResponse(retryResponse);
    if (!retryResponse.ok || !retryData.ok) {
      throwApiError(retryData, retryResponse.status);
    }
    return retryData;
  }

  async function apiGet(path) {
    const response = await fetchWithTimeout(path, {
      credentials: 'same-origin',
      headers: editSessionHeaders(),
    });
    const data = await readJsonResponse(response);
    if (!response.ok || !data.ok) {
      throwApiError(data, response.status);
    }
    return data;
  }

  async function apiPost(path, body, options: { keepalive?: boolean } = {}) {
    const payload = JSON.stringify(body);
    return sendWithCsrfRetry((token) =>
      fetchWithTimeout(path, {
        method: 'POST',
        // テーマ変更のような小さな設定保存は、選択直後にページを再読み込みしても
        // ブラウザが送信を中断しないようにする。
        keepalive: Boolean(options.keepalive),
        credentials: 'same-origin',
        headers: {
          ...editSessionHeaders(),
          'Content-Type': 'application/json',
          'X-CSRF-Token': token,
        },
        body: payload,
      })
    );
  }

  async function apiUpload(path, file) {
    return sendWithCsrfRetry((token) => {
      const formData = new FormData();
      formData.append('file', file);
      return fetchWithTimeout(
        path,
        {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            ...editSessionHeaders(),
            'X-CSRF-Token': token,
          },
          body: formData,
        },
        uploadTimeoutMs
      );
    });
  }

  return { apiGet, apiPost, apiUpload, editSessionHeaders, refreshCsrfToken };
}
